// @givmo/fundraisers-module — a campaign's Social posts as the backend serves them for the appeal's Social
// tab (EMC-WEB5, over `GET /org/{org_ref}/fundraisers/{fundraiser_id}/social`: Social's renter read,
// rented under the Fundraisers gate, D-EMC-1).
//
// ⚠ EVERY FACT IS SOCIAL'S. Whether Social serves here is the read's `available`; a post is its time, an
// excerpt, its image's public address where it has one (SMM-BE22's optional `image_url`) and each
// account's state, and nothing else leaves Social this way (no author, failure detail or engagement
// figure). This file reads what the server answered and never throws: an entry it cannot read is left
// out, and an account state it doesn't know reads as one that needs a look.

import { isGivmoError } from '@givmo/api-client';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

/**
 * Where one account's post stands, in Social's own vocabulary: its coarse `state` (SMM-BE22: `deferred`
 * is held by the daily limit, `unknown` failed with its outcome unconfirmed, `removed` the Givmo
 * channel's). `attention` is the tab's word for a state this version doesn't know: a post that did not
 * publish may yet have posted, so it never reads "Didn't post" until Social says which.
 */
export type SocialAccountState =
  | 'draft'
  | 'scheduled'
  | 'deferred'
  | 'posting'
  | 'published'
  | 'unknown'
  | 'failed'
  | 'removed'
  | 'attention';

const COARSE_STATES: ReadonlySet<string> = new Set([
  'draft',
  'scheduled',
  'deferred',
  'posting',
  'published',
  'unknown',
  'failed',
  'removed',
]);

/** Social's raw variant status, read only where its coarse `state` is absent. */
const RAW_STATUSES: Readonly<Record<string, SocialAccountState>> = {
  draft: 'draft',
  queued: 'scheduled',
  publishing: 'posting',
  published: 'published',
};

export interface SocialAccountRow {
  platform: string;
  accountName: string | null;
  state: SocialAccountState;
  /** The post on the platform, for a published one; null where Social holds none. */
  permalink: string | null;
}

export interface SocialPostRow {
  postId: string;
  /** When it went out, or goes out; null for a draft. */
  at: string | null;
  excerpt: string;
  /** The post's image (its first), a public address; null for a post without one, or before Social
   *  serves it (SMM-BE22). */
  imageUrl: string | null;
  accounts: SocialAccountRow[];
}

export interface CampaignSocial {
  /** Social offered to the organization and switched on. False: no tab. */
  available: boolean;
  posts: SocialPostRow[];
}

export const NO_CAMPAIGN_SOCIAL: CampaignSocial = { available: false, posts: [] };

export function accountState(variant: Record<string, unknown>): SocialAccountState {
  const coarse = text(variant.state);
  if (coarse !== null && COARSE_STATES.has(coarse)) return coarse as SocialAccountState;
  const raw = text(variant.status);
  return (raw !== null ? RAW_STATUSES[raw] : undefined) ?? 'attention';
}

function decodeAccount(value: unknown): SocialAccountRow | null {
  if (!isRecord(value)) return null;
  const platform = text(value.platform);
  if (platform === null) return null;
  const permalink = text(value.external_permalink);
  return {
    platform,
    accountName: text(value.account_name),
    state: accountState(value),
    permalink: permalink !== null && /^https:\/\//i.test(permalink) ? permalink : null,
  };
}

function decodePost(value: unknown): SocialPostRow | null {
  if (!isRecord(value)) return null;
  const postId = text(value.post_id);
  if (postId === null) return null;
  const at = text(value.at);
  const image = text(value.image_url);
  return {
    postId,
    at: at !== null && !Number.isNaN(new Date(at).getTime()) ? at : null,
    excerpt: typeof value.excerpt === 'string' ? value.excerpt : '',
    // Only a web address is an image here; anything else reads as no image.
    imageUrl: image !== null && /^https?:\/\//i.test(image) ? image : null,
    accounts: (Array.isArray(value.variants) ? value.variants : [])
      .map(decodeAccount)
      .filter((row): row is SocialAccountRow => row !== null),
  };
}

/** The read's answer, tolerantly: anything it cannot read is no Social here. */
export function decodeCampaignSocial(value: unknown): CampaignSocial {
  if (!isRecord(value) || value.available !== true) return NO_CAMPAIGN_SOCIAL;
  return {
    available: true,
    posts: (Array.isArray(value.entries) ? value.entries : [])
      .map(decodePost)
      .filter((row): row is SocialPostRow => row !== null),
  };
}

/** A post with no time yet: every account a draft (or none named, and no time). */
export function isDraftPost(post: SocialPostRow): boolean {
  return post.accounts.length > 0
    ? post.accounts.every((account) => account.state === 'draft')
    : post.at === null;
}

/**
 * The list's order (after the user test, 2026-11-03): the drafts grouped first, where their writer left
 * them, then every other post by when it goes out or went out, the earliest first; a post with no time
 * that isn't a draft goes last.
 */
export function groupPosts(posts: readonly SocialPostRow[]): {
  drafts: SocialPostRow[];
  dated: SocialPostRow[];
} {
  const time = (row: SocialPostRow) =>
    row.at === null ? Number.POSITIVE_INFINITY : new Date(row.at).getTime();
  return {
    drafts: posts.filter(isDraftPost),
    dated: posts.filter((row) => !isDraftPost(row)).sort((a, b) => time(a) - time(b)),
  };
}

/** A route miss or a refusal: Social isn't served here (email campaigns dark, or not this campaign). */
export function isCampaignSocialUnavailable(error: unknown): boolean {
  return isGivmoError(error) && (error.status === 404 || error.status === 403);
}
